import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/3f757860/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import Preview from 'components/Preview';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const InlineNotification = makeShortcode("InlineNotification");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3>{`Starting with the Carbon Angular, there are two ways to begin working with Carbon components. By the end, these two methods will produce the same result.`}</h3>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Prerequisites</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Use starter app - recommended</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Start from scratch</AnchorLink>
    </AnchorLinks>
    <h2>{`Preview`}</h2>
    <p>{`A `}<a parentName="p" {...{
        "href": "https://angular-step-2-carbon-tutorial.netlify.com"
      }}>{`preview`}</a>{` of what you will build:`}</p>
    <Preview height="200" title="Carbon Angular Tutorial Step 1" src="https://angular-step-2-carbon-tutorial.netlify.com" frameBorder="no" allowtransparency="true" allowFullScreen={true} mdxType="Preview" />
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If you get lint errors when you copy the code from the snippets, run `}<inlineCode parentName="p">{`ng lint --fix`}</inlineCode>{` to fix them.`}</p>
    </InlineNotification>
    <h2>{`Prerequisites`}</h2>
    <h3>{`Fork, clone and branch`}</h3>
    <p>{`This tutorial has an accompanying GitHub repository called `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-tutorial-angular"
      }}>{`carbon-tutorial-angular`}</a>{` that we’ll use as a starting point for each step.`}</p>
    <h3>{`Fork`}</h3>
    <p>{`To begin, fork `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-tutorial-angular"
      }}>{`carbon-tutorial-angular`}</a>{` using your GitHub account.`}</p>
    <h3>{`Clone`}</h3>
    <p>{`Go to your forked repository, copy the SSH or HTTPS URL and in your terminal run the two commands to get the repository in your local file system and enter that directory.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git clone [your fork SSH/HTTPS]
cd carbon-tutorial-angular
`}</code></pre>
    <h3>{`Add upstream remote`}</h3>
    <p>{`Add a remote called `}<inlineCode parentName="p">{`upstream`}</inlineCode>{` so we can eventually submit a pull request once you have completed this tutorial step.`}</p>
    <h5>{`SSH:`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git remote add upstream git@github.com:carbon-design-system/carbon-tutorial-angular.git
`}</code></pre>
    <p>{`Or, if you prefer to use HTTPS instead of SSH with your remotes:`}</p>
    <h5>{`HTTPS:`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git remote add upstream https://github.com/carbon-design-system/carbon-tutorial-angular.git
`}</code></pre>
    <p>{`Verify that your forked repository remotes are correct:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git remote -v
`}</code></pre>
    <p>{`Your terminal should output something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`origin  [your forked repo] (fetch)
origin  [your forked repo] (push)
upstream    git@github.com:carbon-design-system/carbon-tutorial-angular.git (fetch)
upstream    git@github.com:carbon-design-system/carbon-tutorial-angular.git (push)
`}</code></pre>
    <h3>{`Branch`}</h3>
    <p>{`Now that we have our repository set up, let’s check out the branch for this tutorial step’s starting point. Run the two commands:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git fetch upstream
git checkout -b angular-step-1 upstream/angular-step-1
`}</code></pre>
    <h2>{`Use starter app (recommended)`}</h2>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Add starter remote</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Pull starter code</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Remove starter remote</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Build and start</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Update UI Shell</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Create pages</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Add routing</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Submit pull request</AnchorLink>
    </AnchorLinks>
    <h3>{`Add starter remote`}</h3>
    <p>{`Add a temporary remote called `}<inlineCode parentName="p">{`starter`}</inlineCode>{` so we can clone the `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-angular-starter"
      }}>{`carbon-angular-starter`}</a>{`.`}</p>
    <h5>{`SSH:`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git remote add starter git@github.com:carbon-design-system/carbon-angular-starter.git
`}</code></pre>
    <p>{`Or, if you prefer to use HTTPS instead of SSH with your remotes:`}</p>
    <h5>{`HTTPS:`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git remote add starter https://github.com/carbon-design-system/carbon-angular-starter.git
`}</code></pre>
    <h3>{`Pull starter code`}</h3>
    <p>{`Now that we a have starter remote, we can rebase our branch to match whatever is in the starter app. Run the two commands:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git fetch starter
git pull --allow-unrelated-histories starter master
`}</code></pre>
    <h3>{`Remove starter remote`}</h3>
    <p>{`Remove the temporary remote called `}<inlineCode parentName="p">{`starter`}</inlineCode>{` since we have no use for it anymore.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git remote remove starter
`}</code></pre>
    <h2>{`Build and start`}</h2>
    <p>{`We have the repository forked to your GitHub account, cloned down to your machine, and the starting branch checked out. Next, install the Carbon app’s dependencies with:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install
`}</code></pre>
    <p>{`After the dependencies are installed, you can start the app with:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm start
`}</code></pre>
    <p>{`Your app should now be running with the message: `}<inlineCode parentName="p">{`** Angular Live Development Server is listening on localhost:4200, open your browser on http://localhost:4200/ **`}</inlineCode></p>
    <p>{`That’s it! Your browser should now resemble our `}<a parentName="p" {...{
        "href": "https://carbon-angular-starter.netlify.com/"
      }}>{`starter-app`}</a>{`.`}</p>
    <h2>{`Update UI Shell`}</h2>
    <p>{`Let’s update the UI Shell so it has a link to the repos page that we will create shortly. In `}<inlineCode parentName="p">{`header.component.html`}</inlineCode>{` replace the code with:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/header/header.component.html",
        "path": "src/app/header/header.component.html"
      }}>{`<ibm-header [route]="['/']" name="Carbon Tutorial Angular">
  <ibm-header-navigation ariaLabel="Carbon Tutorial Angular">
    <ibm-header-item [route]="'[/repos']">Repositories</ibm-header-item>
  </ibm-header-navigation>
  <ibm-header-global>
    <ibm-header-action title="action">
      <svg icon ibmIconNotification20></svg>
    </ibm-header-action>
    <ibm-header-action title="action">
      <svg icon ibmIconUserAvatar20></svg>
    </ibm-header-action>
    <ibm-header-action title="action">
      <svg icon ibmIconAppSwitcher20></svg>
    </ibm-header-action>
  </ibm-header-global>
</ibm-header>
`}</code></pre>
    <p>{`Before we continue on to the next step, we want to begin developing with a clean app. So let’s delete the `}<inlineCode parentName="p">{`src/app/starter-home`}</inlineCode>{` folder and update the routing in `}<inlineCode parentName="p">{`app-routing.module.ts`}</inlineCode>{` by removing the below block:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app-routing.module.ts",
        "path": "src/app/app-routing.module.ts"
      }}>{`{
  path: '',
  loadChildren: () => import('./starter-home/starter-home.module').then(m => m.StarterHomeModule)
},
`}</code></pre>
    <p>{`Now you can skip most of `}<a parentName="p" {...{
        "href": "#start-from-scratch"
      }}>{`Start from scratch`}</a>{` and jump straight to `}<a parentName="p" {...{
        "href": "#create-pages"
      }}>{`Create pages`}</a>{`.`}</p>
    <h2>{`Start from scratch`}</h2>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Install Angular CLI</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Create an Angular App</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Install Carbon</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Code style</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Progressive web app</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Run the app</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Add UI Shell</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Create pages</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Add routing</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Submit pull request</AnchorLink>
    </AnchorLinks>
    <h2>{`Install Angular CLI`}</h2>
    <p>{`Since we are starting from scratch, we need to first install Angular CLI.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install -g @angular/cli
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If you are using macOS you might need to execute as a `}<inlineCode parentName="p">{`sudo`}</inlineCode>{` command.`}</p>
    </InlineNotification>
    <h2>{`Create an Angular App`}</h2>
    <p>{`Now that we have our environment set up, starting a new Angular app is easy! If you haven’t set up the environment yet, please do so using the steps provided in Prerequisites (above). We will be using the Angular CLI to create and generate our components. It can also generate services, router, components, and directives.`}</p>
    <p>{`To create a new Angular project with Angular CLI, just run:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng new carbon-angular-tutorial --directory ./
`}</code></pre>
    <p>{`This will create the new project within the current directory. Make sure you do this within the cloned fork of the project.
When you get prompted, enter the following.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`? Would you like to add Angular routing? Yes
? Which stylesheet format would you like to use? SCSS
`}</code></pre>
    <p>{`This command will install the Angular app with all the configurations needed. Within the project folder `}<inlineCode parentName="p">{`carbon-angular-tutorial`}</inlineCode>{`, the `}<inlineCode parentName="p">{`src`}</inlineCode>{` directory should have the following structure:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`carbon-angular-tutorial
...
  ├── src
    ├── app
    │   ├── app-routing.module.ts
    │   ├── app.component.html
    │   ├── app.component.scss
    │   ├── app.component.spec.ts
    │   ├── app.component.ts
    │   └── app.module.ts
    ├── assets
    ├── environments
    │   ├── environment.prod.ts
    │   └── environment.ts
    ├── favicon.ico
    ├── index.html
    ├── main.ts
    ├── polyfills.ts
    ├── styles.scss
    └── test.ts
`}</code></pre>
    <h2>{`Install Carbon`}</h2>
    <p>{`Even though we installed some dependencies while creating the new app, we’ve yet to install the Carbon packages.`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`carbon-components`}</inlineCode>{` - component styles`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`carbon-components-angular`}</inlineCode>{` - Angular components`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`@carbon/icons-angular`}</inlineCode>{` - Angular icons`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install carbon-components carbon-components-angular @carbon/icons-angular
`}</code></pre>
    <h3>{`Import carbon-component styles`}</h3>
    <p>{`In `}<inlineCode parentName="p">{`src/styles.scss`}</inlineCode>{`, import the Carbon styles by adding the following to the top of the file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scss",
        "metastring": "path=src/styles.scss",
        "path": "src/styles.scss"
      }}>{`@import '~carbon-components/scss/globals/scss/styles';
`}</code></pre>
    <h2>{`Code style`}</h2>
    <p>{`Next, we will edit the code style to match Carbon Angular recommendations. Through the conventions listed in our `}<inlineCode parentName="p">{`tslint.js`}</inlineCode>{`, the app will look clean and easier to read and maintain. So replace your code in `}<inlineCode parentName="p">{`tslint.json`}</inlineCode>{` with the code below.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=tslint.json",
        "path": "tslint.json"
      }}>{`{
    "rulesDirectory": [
    "node_modules/codelyzer"
    ],
    "rules": {
        "callable-types": true,
        "class-name": true,
        "comment-format": [
            true,
            "check-space"
        ],
        "curly": true,
        "eofline": true,
        "forin": true,
        "import-blacklist": [true],
        "import-spacing": true,
        "indent": [
            true,
            "tabs",
            2
        ],
        "interface-over-type-literal": true,
        "label-position": true,
        "max-line-length": [
            true,
            140
        ],
        "member-access": false,
        "member-ordering": [
            true,
            {
                "order": [
                    "public-static-field",
                    "protected-static-field",
                    "private-static-field",
                    "public-static-method",
                    "protected-static-method",
                    "private-static-method",
                    "public-instance-field",
                    "protected-instance-field",
                    "private-instance-field",
                    "public-constructor",
                    "protected-constructor",
                    "private-constructor",
                    "public-instance-method",
                    "protected-instance-method",
                    "private-instance-method"
                ]
            }
        ],
        "no-arg": true,
        "no-bitwise": true,
        "no-console": [
            true,
            "debug",
            "info",
            "time",
            "timeEnd",
            "trace"
        ],
        "no-construct": true,
        "no-debugger": true,
        "no-duplicate-variable": true,
        "no-empty": false,
        "no-empty-interface": true,
        "no-eval": true,
        "no-inferrable-types": [true, "ignore-params"],
        "no-shadowed-variable": true,
        "no-string-literal": false,
        "no-string-throw": true,
        "no-switch-case-fall-through": true,
        "no-trailing-whitespace": true,
        "no-unused-expression": true,
        "no-use-before-declare": true,
        "no-var-keyword": true,
        "object-literal-sort-keys": false,
        "one-line": [
            true,
            "check-open-brace",
            "check-catch",
            "check-else",
            "check-whitespace"
        ],
        "prefer-const": true,
        "quotemark": [
            true,
            "single"
        ],
        "radix": true,
        "semicolon": [
            true,
            "always"
        ],
        "triple-equals": [
            true,
            "allow-null-check",
            "allow-undefined-check"
        ],
        "typedef-whitespace": [
            true,
            {
                "call-signature": "nospace",
                "index-signature": "nospace",
                "parameter": "nospace",
                "property-declaration": "nospace",
                "variable-declaration": "nospace"
            }
        ],
        "typeof-compare": true,
        "unified-signatures": true,
        "variable-name": [
            true,
            "ban-keywords",
            "check-format",
            "allow-leading-underscore"
        ],
        "whitespace": [
            true,
            "check-branch",
            "check-decl",
            "check-operator",
            "check-module",
            "check-separator",
            "check-rest-spread",
            "check-type",
            "check-preblock"
        ],

        "directive-selector": [true, "attribute", ["app", "test"], "camelCase"],
        "component-selector": [true, "element", ["app", "test"], "kebab-case"],

        "no-inputs-metadata-property": true,
        "no-outputs-metadata-property": true,
        "no-host-metadata-property": true,
        "no-attribute-decorator": true,
        "no-input-rename": true,
        "no-output-rename": true,
        "no-forward-ref": true,
        "use-lifecycle-interface": true,
        "use-pipe-transform-interface": true,

        "templates-use-public": true,
        "invoke-injectable": true
    }
}
`}</code></pre>
    <p>{`Now that our guidelines are defined, we need to apply them to our app.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng lint --fix
`}</code></pre>
    <p>{`All files should now pass linting. Finally, to help the editor indent lines properly in our project and to keep code styles consistent, we need to edit `}<inlineCode parentName="p">{`.editorconfig`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ini",
        "metastring": "path=.editorconfig",
        "path": ".editorconfig"
      }}>{`# Editor configuration, see https://editorconfig.org
root = true

[*]
charset = utf-8
indent_style = tab
indent_size = 4
insert_final_newline = true
trim_trailing_whitespace = true

[*.md]
max_line_length = off
trim_trailing_whitespace = false
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` This only works with editors that support editorconfigs.`}</p>
    </InlineNotification>
    <h2>{`Progressive web app`}</h2>
    <p>{`Before we start adding components, let’s explore progressive web app, or PWA. PWA is a web application that has a set of capabilities (similar to native apps) which provide an app-like experience to users. Some other capabilities include full responsiveness and browser compatibility, connectivity independence and self-updates. In the `}<inlineCode parentName="p">{`src`}</inlineCode>{` directory create a file called `}<inlineCode parentName="p">{`sw.js`}</inlineCode>{` and paste the code below.`}</p>
    <p>{`For more information on PWAs, check out the `}<a parentName="p" {...{
        "href": "https://developers.google.com/web/progressive-web-apps/"
      }}>{`developer’s documentation`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/sw.js",
        "path": "src/sw.js"
      }}>{`let log = console.log.bind(console);
let err = console.error.bind(console);

let version = '1';
let cacheName = 'pwa-client-v' + version;
let dataCacheName = 'pwa-client-data-v' + version;
let appShellFilesToCache = [
  './',
  './index.html',
  // "./inline.bundle.js",
  // "./polyfills.bundle.js",
  // "./styles.bundle.js",
  // "./vendor.bundle.js",
  // "./main.bundle.js",
  './manifest.json',
];

self.addEventListener('install', ev => {
  ev.waitUntil(self.skipWaiting());
  log('Service Worker: Installed');

  ev.waitUntil(
    caches.open(cacheName).then(cache => {
      log('Service Worker: Caching App Shell');
      return cache.addAll(appShellFilesToCache);
    })
  );
});

self.addEventListener('activate', ev => {
  ev.waitUntil(self.clients.claim());
  log('Service Worker: Active');

  ev.waitUntil(
    caches.keys().then(keyList => {
      return Promise.all(
        keyList.map(key => {
          if (key !== cacheName) {
            log('Service Worker: Removing old cache', key);
            return caches.delete(key);
          }
        })
      );
    })
  );
});

self.addEventListener('fetch', e => {
  log('Service Worker: Fetch URL ', e.request.url);

  // Match requests for data and handle them separately
  e.respondWith(
    caches.match(e.request.clone()).then(response => {
      return (
        response ||
        fetch(e.request.clone()).then(r2 => {
          return caches.open(dataCacheName).then(cache => {
            console.log('Service Worker: Fetched & Cached URL ', e.request.url);
            cache.put(e.request.url, r2.clone());
            return r2.clone();
          });
        })
      );
    })
  );
});
`}</code></pre>
    <p>{`Now we need to register our service worker and use it to cache files locally. In `}<inlineCode parentName="p">{`index.html`}</inlineCode>{` add the below code in a script block using `}<inlineCode parentName="p">{`<script></script>`}</inlineCode>{` under the `}<inlineCode parentName="p">{`app-root`}</inlineCode>{` tag.`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/index.html",
        "path": "src/index.html"
      }}>{`<script>
// Uncomment the following lines to activate the service worker
// if (navigator.serviceWorker) {
//   navigator.serviceWorker.register("sw.js").then(() => {
//     console.log("Service worker installed");
//   }, err => {
//     console.error("Service worker error:", err);
//   });
// }
</script>
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Next, we need to add the web app manifest. The manifest is a file in `}<inlineCode parentName="p">{`json`}</inlineCode>{` format that provides the name, description, icon locations and other information required for an app to be considered a PWA. It lets users install the web app on the home screen just like native apps without going through an app store. Create a `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` in the `}<inlineCode parentName="p">{`src`}</inlineCode>{` directory the paste the following code.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/manifest.json",
        "path": "src/manifest.json"
      }}>{`{
  "name": "Carbon Angular Starter",
  "short_name": "Starter",
  "icons": [
    {
      "src": "/assets/icons/favicon-32x32.png",
      "sizes": "32x32",
      "type": "image/png"
    },
    {
      "src": "/assets/icons/android-chrome-192x192.png",
      "sizes": "192x192",
      "type": "image/png"
    },
    {
      "src": "/assets/icons/icon-144x144.png",
      "sizes": "192x192",
      "type": "image/png"
    },
    {
      "src": "/assets/icons/icon-192x192.png",
      "sizes": "192x192",
      "type": "image/png"
    },
    {
      "src": "/assets/icons/icon-256x256.png",
      "sizes": "256x256",
      "type": "image/png"
    },
    {
      "src": "/assets/icons/icon-384x384.png",
      "sizes": "384x384",
      "type": "image/png"
    },
    {
      "src": "/assets/icons/icon-512x512.png",
      "sizes": "512x512",
      "type": "image/png"
    }
  ],
  "background_color": "#ff00ff",
  "theme_color": "#ffff00",
  "display": "standalone",
  "start_url": "/"
}
`}</code></pre>
    <p>{`We will also need to add `}<inlineCode parentName="p">{`sw.js`}</inlineCode>{` and `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` into our asset entities (build & test) in `}<inlineCode parentName="p">{`angular.json`}</inlineCode>{` and reference the `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` and `}<inlineCode parentName="p">{`theme-color`}</inlineCode>{` in the `}<inlineCode parentName="p">{`index.html`}</inlineCode>{` `}<inlineCode parentName="p">{`<head>`}</inlineCode>{` tags. The theme color tells the browser with what color to tint UI elements such as the address bar.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=angular.json",
        "path": "angular.json"
      }}>{`"assets": [
  "src/favicon.ico",
  "src/assets",
  "src/sw.js",
  "src/manifest.json"
],
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/index.html",
        "path": "src/index.html"
      }}>{`<link rel="manifest" href="manifest.json" />
<meta name="theme-color" content="#ffff00" />
`}</code></pre>
    <p>{`Finally, let’s download the different sizes of icons to the `}<inlineCode parentName="p">{`src/assests/icons`}</inlineCode>{` directory by running the following commands:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`mkdir src/assets/icons
curl -o src/assets/icons/android-chrome-192x192.png https://raw.githubusercontent.com/carbon-design-system/carbon-angular-starter/master/src/assets/icons/android-chrome-192x192.png
curl -o src/assets/icons/icon-144x144.png https://raw.githubusercontent.com/carbon-design-system/carbon-angular-starter/master/src/assets/icons/icon-144x144.png
curl -o src/assets/icons/icon-192x192.png https://raw.githubusercontent.com/carbon-design-system/carbon-angular-starter/master/src/assets/icons/icon-192x192.png
curl -o src/assets/icons/icon-256x256.png https://raw.githubusercontent.com/carbon-design-system/carbon-angular-starter/master/src/assets/icons/icon-256x256.png
curl -o src/assets/icons/icon-384x384.png https://raw.githubusercontent.com/carbon-design-system/carbon-angular-starter/master/src/assets/icons/icon-384x384.png
curl -o src/assets/icons/icon-512x512.png https://raw.githubusercontent.com/carbon-design-system/carbon-angular-starter/master/src/assets/icons/icon-512x512.png
`}</code></pre>
    <h2>{`Run the app`}</h2>
    <p>{`Now we can run our app for a quick preview inside the browser.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm start
`}</code></pre>
    <p>{`Your app should now be running with the message: `}<inlineCode parentName="p">{`** Angular Live Development Server is listening on localhost:4200, open your browser on http://localhost:4200/ **`}</inlineCode></p>
    <p>{`Before we start adding components we want to start with an empty project, so delete everything in `}<inlineCode parentName="p">{`app.component.html`}</inlineCode>{` except for the `}<inlineCode parentName="p">{`router-outler`}</inlineCode>{`. We will also have to delete the test that was associated with this code. So in `}<inlineCode parentName="p">{`app.component.spec.ts`}</inlineCode>{`, delete the `}<inlineCode parentName="p">{`should render title`}</inlineCode>{` and `}<inlineCode parentName="p">{`should have as title 'carbon-angular-tutorial'`}</inlineCode>{` test.`}</p>
    <h2>{`Add UI Shell`}</h2>
    <p>{`Next, we’re going to create an Angular component called `}<inlineCode parentName="p">{`Header`}</inlineCode>{` to use with the UI Shell Carbon component. Using Angular CLI we will create this component inside the `}<inlineCode parentName="p">{`src/app`}</inlineCode>{` directory.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng g component header --lint-fix
`}</code></pre>
    <h5>{`Folder structure`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`src/app/header
├── header.component.html
├── header.component.scss
├── header.component.spec.ts
└── header.component.ts
`}</code></pre>
    <h3>{`Import UI Shell`}</h3>
    <p>{`Next we’ll import our Carbon UI Shell components into `}<inlineCode parentName="p">{`app.module.ts`}</inlineCode>{`, `}<inlineCode parentName="p">{`app.component.spec.ts`}</inlineCode>{` and `}<inlineCode parentName="p">{`header.component.spec.ts`}</inlineCode>{`. Set up the file like so:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app.module.ts",
        "path": "src/app/app.module.ts"
      }}>{`import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';

// carbon-components-angular default imports
import { UIShellModule } from 'carbon-components-angular';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    UIShellModule
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app.component.spec.ts,src/app/header/header.component.spec.ts",
        "path": "src/app/app.component.spec.ts,src/app/header/header.component.spec.ts"
      }}>{`import { UIShellModule } from 'carbon-components-angular/ui-shell/ui-shell.module';
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app.component.spec.ts,src/app/header/header.component.spec.ts",
        "path": "src/app/app.component.spec.ts,src/app/header/header.component.spec.ts"
      }}>{`TestBed.configureTestingModule({
  declarations: [HeaderComponent],
  imports: [UIShellModule]
});
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` You can find a description of the different components used in UI Shell in our `}<a parentName="p" {...{
          "href": "https://github.com/IBM/carbon-components-angular/tree/master/src/ui-shell"
        }}>{`carbon-components-angular`}</a>{` package.`}</p>
    </InlineNotification>
    <h3>{`Import icons`}</h3>
    <p>{`Now let’s import the icons from our `}<inlineCode parentName="p">{`@carbon/icons-angular`}</inlineCode>{` package. In `}<inlineCode parentName="p">{`app.module.ts`}</inlineCode>{`, `}<inlineCode parentName="p">{`app.component.spec.ts`}</inlineCode>{` and `}<inlineCode parentName="p">{`header.component.spec.ts`}</inlineCode>{`, we need to import each individual icon we will use.`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app.module.ts,src/app/app.component.spec.ts,src/app/header/header.component.spec.ts",
        "path": "src/app/app.module.ts,src/app/app.component.spec.ts,src/app/header/header.component.spec.ts"
      }}>{`import { Notification20Module } from '@carbon/icons-angular/lib/notification/20';
import { UserAvatar20Module } from '@carbon/icons-angular/lib/user--avatar/20';
import { AppSwitcher20Module } from '@carbon/icons-angular/lib/app-switcher/20';
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app.module.ts,src/app/app.component.spec.ts,src/app/header/header.component.spec.ts",
        "path": "src/app/app.module.ts,src/app/app.component.spec.ts,src/app/header/header.component.spec.ts"
      }}>{`imports: [Notification20Module, UserAvatar20Module, AppSwitcher20Module];
`}</code></pre>
    <p>{`Then we need to add the template code. Populate `}<inlineCode parentName="p">{`header.component.html`}</inlineCode>{` with:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/header/header.component.html",
        "path": "src/app/header/header.component.html"
      }}>{`<ibm-header name="Carbon Tutorial Angular">
    <ibm-header-navigation ariaLabel="Carbon Tutorial Angular">
        <ibm-header-item href="/repos">Repositories</ibm-header-item>
    </ibm-header-navigation>
    <ibm-header-global>
        <ibm-header-action title="action">
            <svg icon ibmIconNotification20></svg>
        </ibm-header-action>
        <ibm-header-action title="action">
            <svg icon ibmIconUserAvatar20></svg>
        </ibm-header-action>
        <ibm-header-action title="action">
            <svg icon ibmIconAppSwitcher20></svg>
        </ibm-header-action>
    </ibm-header-global>
</ibm-header>
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Next import the header compoenent in `}<inlineCode parentName="p">{`app.component.spec.ts`}</inlineCode>{` and add the component in `}<inlineCode parentName="p">{`app.component.html`}</inlineCode></p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app.component.spec.ts",
        "path": "src/app/app.component.spec.ts"
      }}>{`import { HeaderComponent } from './header/header.component';
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/app.component.spec.ts",
        "path": "src/app/app.component.spec.ts"
      }}>{`declarations: [HeaderComponent];
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/app.component.html",
        "path": "src/app/app.component.html"
      }}>{`<app-header></app-header>
<main class="bx--content">
  <router-outlet></router-outlet>
</main>
`}</code></pre>
    <p>{`Let’s add some padding to the top of the document, so the content is below the header. We are going to do this by using the `}<inlineCode parentName="p">{`bx--header`}</inlineCode>{` class provided by carbon. So in `}<inlineCode parentName="p">{`header.component.ts`}</inlineCode>{` lets hostbind that class.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import { Component, HostBinding } from '@angular/core';
  ...
    @HostBinding('class.bx--header') headerClass = true;
`}</code></pre>
    <h2>{`Create pages`}</h2>
    <p>{`Next thing we need to do is create the files for our content. These files will be located in the `}<inlineCode parentName="p">{`app`}</inlineCode>{` folder inside of `}<inlineCode parentName="p">{`src`}</inlineCode>{`. It should be a sibling of `}<inlineCode parentName="p">{`header`}</inlineCode>{`.`}</p>
    <p>{`Our app will have two pages. First, we need a landing page. Go ahead and stop your development server (with `}<inlineCode parentName="p">{`CTRL-C`}</inlineCode>{`) and then:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng g module home --routing --lint-fix
ng g component home/landing-page --lint-fix
`}</code></pre>
    <h5>{`Folder structure`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`src/app/home
├── home
│   ├── landing-page.component.html
│   ├── landing-page.component.scss
│   ├── landing-page.component.spec.ts
│   └── landing-page.component.ts
├── home-routing.module.ts
└── home-page.module.ts
`}</code></pre>
    <p>{`And a repo page:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng g module repositories --routing --lint-fix
ng g component repositories/repo-page --lint-fix
`}</code></pre>
    <h5>{`Folder structure`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`src/app/repositories
├── repositories
│   ├── repo-page.component.html
│   ├── repo-page.component.scss
│   ├── repo-page.component.spec.ts
│   └── repo-page.component.ts
├── repositories-routing.module.ts
└── repositories.module.ts
`}</code></pre>
    <p>{`Now you can restart your server with `}<inlineCode parentName="p">{`npm start`}</inlineCode>{`.`}</p>
    <h2>{`Add routing`}</h2>
    <p>{`We need to update routing functionality to enable the loading of `}<inlineCode parentName="p">{`repositories`}</inlineCode>{`. Inside `}<inlineCode parentName="p">{`app-routing.module.ts`}</inlineCode>{` we’ll add the following code in the routes array:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app-routing.module.ts",
        "path": "src/app-routing.module.ts"
      }}>{`const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
  },
  {
    path: 'repos',
    loadChildren: () =>
      import('./repositories/repositories.module').then(
        m => m.RepositoriesModule
      ),
  },
];
`}</code></pre>
    <p>{`And modify the `}<inlineCode parentName="p">{`NgModule`}</inlineCode>{` declaration to use the hash router:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app-routing.module.ts",
        "path": "src/app-routing.module.ts"
      }}>{`@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
`}</code></pre>
    <p>{`And add routes for the landing and repo pages:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/home/home-routing.module.ts",
        "path": "src/app/home/home-routing.module.ts"
      }}>{`import { LandingPageComponent } from './landing-page/landing-page.component';

const routes: Routes = [
  {
    path: '',
    component: LandingPageComponent,
  },
];
`}</code></pre>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "path=src/app/repositories/repositories-routing.module.ts",
        "path": "src/app/repositories/repositories-routing.module.ts"
      }}>{`import { RepoPageComponent } from './repo-page/repo-page.component';

const routes: Routes = [
  {
    path: '',
    component: RepoPageComponent
  }
];
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`After that we need to do a couple quick fixes to the UI Shell to have it route to the repo page.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/header/header.component.html",
        "path": "src/app/header/header.component.html"
      }}>{`<ibm-header-item [route]="['/repos']">Repositories</ibm-header-item>
`}</code></pre>
    <p>{`You should now have a working header that routes to the repos pages without full page reload!`}</p>
    <h2>{`Submit pull request`}</h2>
    <p>{`We’re going to submit a pull request to verify completion of this tutorial step and demonstrate a couple related concepts.`}</p>
    <h3>{`Continuous integration (CI) check`}</h3>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Before you run any tests, make sure that you are using ChromeHeadless in `}<inlineCode parentName="p">{`karma.conf.js`}</inlineCode>{` instead of Chrome.`}</p>
    </InlineNotification>
    <p>{`We have `}<inlineCode parentName="p">{`lint`}</inlineCode>{` and `}<inlineCode parentName="p">{`test`}</inlineCode>{` scripts defined in `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` that verify file formatting for files that have been touched since the last Git commit. You’d typically also have that script run your test suite as part of your CI build. Go ahead and make sure everything looks good with:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng lint --fix
npm run lint && npm test
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If this gives an error, it’s likely that some of your source files are not properly formatted.`}</p>
    </InlineNotification>
    <h3>{`Git commit and push`}</h3>
    <p>{`Before we can create a pull request, we need to stage and commit all of our changes:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git add --all && git commit -m "feat(tutorial): complete step 1"
`}</code></pre>
    <p>{`Then, push to your repository:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git push origin angular-step-1
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If your Git remote protocol is HTTPS instead of SSH, you may be prompted to authenticate with GitHub when you push changes. If your GitHub account has two-factor authentication enabled, we recommend that you follow these instructions to `}<a parentName="p" {...{
          "href": "https://help.github.com/en/articles/creating-a-personal-access-token-for-the-command-line"
        }}>{`create a personal access token for the command line`}</a>{`. That lets you use your token instead of password when performing Git operations over HTTPS.`}</p>
    </InlineNotification>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If you receive a `}<inlineCode parentName="p">{`non-fast-forward`}</inlineCode>{` error, it’s likely that your forked repository is behind the original repository and needs updated. This can happen if the tutorial was updated after you began working on it. To fix, run `}<inlineCode parentName="p">{`git pull upstream angular-step-1`}</inlineCode>{` to merge the changes into your branch, then you can try pushing again. Or, you can `}<a parentName="p" {...{
          "href": "https://help.github.com/en/articles/syncing-a-fork"
        }}>{`manually merge`}</a>{` in the upstream changes.`}</p>
    </InlineNotification>
    <h3>{`Pull request (PR)`}</h3>
    <p>{`Finally, visit `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-tutorial-angular"
      }}>{`carbon-tutorial-angular`}</a>{` to “Compare & pull request”. In doing so, make sure that you are comparing to `}<inlineCode parentName="p">{`angular-step-1`}</inlineCode>{` into `}<inlineCode parentName="p">{`base: angular-step-1`}</inlineCode>{`. Take notice of the `}<a parentName="p" {...{
        "href": "https://www.netlify.com"
      }}>{`Netlify`}</a>{` bot that deploys a preview of your PR every time that you push new commits. These previews can be shared and viewed by anybody to assist the PR review process.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Expect your tutorial step PRs to be reviewed by the Carbon team but not merged. We’ll close your PR so we can keep the repository’s remote branches pristine and ready for the next person!`}</p>
    </InlineNotification>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      